/**
 * DateRangePicker — popover content for the DATES pill.
 *
 * Single inline Calendar in `range` mode for round-trip, single mode for
 * one-way. Emits canonical `YYYY-MM-DD` strings (matches the
 * SearchState.{departureDate,returnDate} type). Multi-city callers should
 * not render the DATES pill at all — handled by EditablePillRow.
 */

import { useMemo } from 'react'
import { format, isValid, parseISO, startOfToday } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { Calendar } from '@/components/ui/calendar'
import type { TripType } from '@/lib/search/types'

interface DateRangePickerProps {
  departureDate?: string
  returnDate?: string
  tripType: TripType
  onChange: (next: { departureDate?: string; returnDate?: string }) => void
}

export function DateRangePicker({
  departureDate,
  returnDate,
  tripType,
  onChange,
}: DateRangePickerProps) {
  const today = useMemo(() => startOfToday(), [])
  const departDate = parseDate(departureDate)
  const retDate = parseDate(returnDate)

  if (tripType === 'one-way') {
    return (
      <div className="sc-picker" data-date-picker="one-way">
        <div className="sc-picker-title">Departure</div>
        <Calendar
          mode="single"
          selected={departDate}
          onSelect={(date) =>
            onChange({
              departureDate: formatDate(date),
              returnDate: undefined,
            })
          }
          disabled={(date) => date < today}
          initialFocus
        />
      </div>
    )
  }

  const range: DateRange | undefined =
    departDate || retDate ? { from: departDate, to: retDate } : undefined

  return (
    <div className="sc-picker" data-date-picker="range">
      <div className="sc-picker-title">Travel dates</div>
      <Calendar
        mode="range"
        numberOfMonths={2}
        selected={range}
        onSelect={(next) => {
          onChange({
            departureDate: formatDate(next?.from),
            returnDate: formatDate(next?.to),
          })
        }}
        disabled={(date) => date < today}
        initialFocus
      />
    </div>
  )
}

function parseDate(s: string | undefined): Date | undefined {
  if (!s) return undefined
  const d = parseISO(s)
  return isValid(d) ? d : undefined
}

function formatDate(d: Date | undefined): string | undefined {
  if (!d || !isValid(d)) return undefined
  return format(d, 'yyyy-MM-dd')
}
